import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { MenuItem } from '@/types'

import { useState } from 'react'
import { getMenu, getSignatureItems, getTodaysBake } from '@/lib/menu'
import { bakeryStructuredData } from '@/lib/structured-data'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ItemSheet } from '@/components/generals/item-sheet'
import { Hero } from '@/components/home/hero'
import { TodaysBake } from '@/components/home/todays-bake'
import { SignatureGrid } from '@/components/home/signature-grid'
import { HowItWorks } from '@/components/home/how-it-works'
import { ProcessSection } from '@/components/home/process-section'
import { CakesTeaser } from '@/components/home/cakes-teaser'
import { VisitSection } from '@/components/home/visit-section'

interface HomeProps {
    todays: Array<MenuItem>
    signature: Array<MenuItem>
    failed: boolean
}

// Props can briefly be missing, for example while the page is swapped in during
// development before its data has loaded, so every list falls back to empty.
const Home: FC<Partial<HomeProps>> = (props): ReactNode => {
    const todays = props.todays ?? []
    const signature = props.signature ?? []
    const failed = props.failed ?? props.todays === undefined
    const [selected, setSelected] = useState<MenuItem | null>(null)

    return (
        <Layout>
            <Seo />

            <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: bakeryStructuredData() }} />

            <Hero />
            <TodaysBake items={todays} failed={failed} onSelect={setSelected} />
            {signature.length > 0 && <SignatureGrid items={signature} onSelect={setSelected} />}
            <HowItWorks />
            <ProcessSection />
            <CakesTeaser />
            <VisitSection />

            <ItemSheet item={selected} onClose={() => setSelected(null)} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const { items } = await getMenu()

        return {
            props: { todays: getTodaysBake(items), signature: getSignatureItems(items), failed: false },
            revalidate: 30
        }
    }
    catch (error) {
        console.error('The home page could not load the menu:', error instanceof Error ? error.message : error)

        return {
            props: { todays: [], signature: [], failed: true },
            revalidate: 10
        }
    }
}

export default Home
